import { useContext, useEffect, useState } from "react";
import AuthContext from "../../context/AuthContext";
import Fetch from "../../services/Fetch";
import PageContent from "../../components/PageContent";
import { useConstants } from "../../hooks/UseConstants";
import { Box, Button, Chip, CircularProgress, Dialog, DialogContent, DialogTitle, MenuItem, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, useTheme } from "@mui/material";
import DashboardPanelCard from "../../components/cards/DashboardPanelCard";
import PageHero from "../../components/motion/PageHero";
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import useSnackBar from "../../hooks/UseSnackBar";
import SnackbarAlert from "../../components/SnackBar";
import { parseApiList, parseApiPagination } from "../../helper/parseApiList";

function Students() {
    const theme = useTheme();
    const { host, language } = useConstants();
    const isAr = language === 'ar';
    const { wait } = useContext(AuthContext);
    const { openSnackBar, type, message, setSnackBar, setOpenSnackBar } = useSnackBar();
    const [loading, setLoading] = useState(true);
    const [students, setStudents] = useState([]);
    const [courses, setCourses] = useState([]);
    const [courseId, setCourseId] = useState('');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [detailOpen, setDetailOpen] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detail, setDetail] = useState(null);
    const [exporting, setExporting] = useState(false);

    const openStudentDetail = async (row) => {
        if (!row.student_id || !row.course_id) return;
        setDetailOpen(true);
        setDetailLoading(true);
        setDetail(null);
        const result = await Fetch(
            `${host}/reports/student/${row.student_id}/course-exams?course_id=${row.course_id}`,
            'GET',
            null
        );
        if (result.status === 200) setDetail(result.data.data);
        setDetailLoading(false);
    };

    const loadCourses = async () => {
        const result = await Fetch(host + `/courses?teacher_id=${localStorage.getItem('id')}&per_page=100`, 'GET', null);
        setCourses(parseApiList(result));
    };

    const loadStudents = async () => {
        setLoading(true);
        let url = `${host}/reports/grades?page=${page}&per_page=15`;
        if (courseId) url += `&course_id=${courseId}`;
        const result = await Fetch(url, 'GET', null);
        const { rows, lastPage } = parseApiPagination(result);
        setStudents(rows);
        setTotalPages(lastPage);
        setLoading(false);
    };

    useEffect(() => {
        if (!wait) loadCourses();
    }, [wait]);

    useEffect(() => {
        if (!wait) loadStudents();
    }, [wait, page, courseId]);

    const downloadExport = async (type) => {
        setExporting(true);
        const params = new URLSearchParams();
        if (courseId) params.set('course_id', courseId);
        const path = type === 'pdf' ? 'grades/export-pdf' : 'grades/export';
        const url = `${host}/reports/${path}${params.toString() ? `?${params}` : ''}`;
        try {
            const res = await fetch(url, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            });
            if (!res.ok) {
                setSnackBar('error', isAr ? 'فشل التصدير' : 'Export failed');
                return;
            }
            const blob = await res.blob();
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = `grades-${new Date().toISOString().slice(0, 10)}.${type === 'pdf' ? 'pdf' : 'xlsx'}`;
            link.click();
            URL.revokeObjectURL(link.href);
            setSnackBar('success', isAr ? 'تم التصدير بنجاح' : 'Exported successfully');
        } catch {
            setSnackBar('error', isAr ? 'تعذر الاتصال بالخادم' : 'Could not reach server');
        }
        setExporting(false);
    };

    const filtered = students.filter((s) => {
        if (!search.trim()) return true;
        const q = search.toLowerCase();
        return (s.student_name || '').toLowerCase().includes(q)
            || (s.course_name || '').toLowerCase().includes(q);
    });

    const courseLabel = (c) => (isAr ? (c.name_ar || c.name_en) : (c.name_en || c.name_ar));

    return (
        <>
            <PageContent wait={wait} loading={loading}>
                <PageHero
                    title={isAr ? 'الطلاب' : 'Students'}
                    subtitle={isAr ? 'درجات وتقدم الطلاب في دوراتك' : 'Student grades and progress in your courses'}
                    icon={<SchoolOutlinedIcon />}
                />
                <DashboardPanelCard
                    title={isAr ? 'فلترة وتصدير' : 'Filter & export'}
                    subtitle={isAr ? 'اختر دورة أو ابحث عن طالب ثم صدّر التقرير' : 'Filter by course or search, then export'}
                    className="!mb-4 mx-0"
                >
                    <Box className="flex flex-wrap gap-3 items-center">
                        <Select
                            displayEmpty
                            value={courseId}
                            onChange={(e) => { setPage(1); setCourseId(e.target.value); }}
                            className="min-w-[200px]"
                            size="small"
                        >
                            <MenuItem value="">{isAr ? 'كل الدورات' : 'All courses'}</MenuItem>
                            {courses.map((c) => (
                                <MenuItem key={c.id} value={String(c.id)}>
                                    {courseLabel(c)}
                                </MenuItem>
                            ))}
                        </Select>
                        <Box className="relative flex-1 min-w-[200px]">
                            <input
                                className="w-full h-10 rounded-md border indent-10 outline-none px-2"
                                placeholder={isAr ? 'بحث باسم الطالب أو الدورة' : 'Search student or course'}
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && setSearch(e.target.value.trim())}
                            />
                            <SearchOutlinedIcon className="absolute top-2 left-2 text-gray-500" />
                        </Box>
                        <Button variant="outlined" onClick={() => downloadExport('excel')} disabled={exporting}>
                            {isAr ? 'تصدير Excel' : 'Export Excel'}
                        </Button>
                        <Button variant="outlined" onClick={() => downloadExport('pdf')} disabled={exporting}>
                            {isAr ? 'تصدير PDF' : 'Export PDF'}
                        </Button>
                    </Box>
                </DashboardPanelCard>

                {loading ? (
                    <Box className="flex justify-center py-16"><CircularProgress /></Box>
                ) : (
                    <Box sx={{ backgroundColor: theme.palette.background.paper }} className="rounded-xl shadow-xl overflow-hidden">
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{isAr ? 'الطالب' : 'Student'}</TableCell>
                                        <TableCell>{isAr ? 'الدورة' : 'Course'}</TableCell>
                                        <TableCell>{isAr ? 'التقدم' : 'Progress'}</TableCell>
                                        <TableCell>{isAr ? 'الدرجة' : 'Grade'}</TableCell>
                                        <TableCell>{isAr ? 'تاريخ التسجيل' : 'Enrolled'}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filtered.length ? filtered.map((row, idx) => (
                                        <TableRow
                                            key={`${row.student_id}-${row.course_id}-${idx}`}
                                            hover
                                            sx={{ cursor: 'pointer' }}
                                            onClick={() => openStudentDetail(row)}
                                        >
                                            <TableCell>{row.student_name}</TableCell>
                                            <TableCell>{row.course_name}</TableCell>
                                            <TableCell>{row.progress_percentage ?? 0}%</TableCell>
                                            <TableCell>{row.final_mark ?? '—'}</TableCell>
                                            <TableCell>{row.enrolled_at?.split(' ')[0]}</TableCell>
                                        </TableRow>
                                    )) : (
                                        <TableRow>
                                            <TableCell colSpan={5} align="center">
                                                {courses.length === 0
                                                    ? (isAr ? 'لا توجد دورات — أنشئ دورة من «دوراتي» أو «إضافة دورة»' : 'No courses — create one from My Courses')
                                                    : (isAr ? 'لا يوجد طلاب مسجّلون بعد في دوراتك' : 'No enrolled students in your courses yet')}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                            <Box className="flex justify-center gap-4 py-3 items-center">
                                <Button size="small" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>‹</Button>
                                <Typography variant="body2">{page} / {totalPages}</Typography>
                                <Button size="small" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>›</Button>
                            </Box>
                        </TableContainer>
                    </Box>
                )}

                <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>{isAr ? 'نتائج الاختبارات' : 'Exam results'}</DialogTitle>
                    <DialogContent>
                        {detailLoading ? (
                            <CircularProgress />
                        ) : detail ? (
                            <Box>
                                <Typography variant="body2" gutterBottom>
                                    {detail.student?.full_name} — {detail.student?.email}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                                    {detail.course?.name}
                                </Typography>
                                {(detail.exams || []).length === 0 ? (
                                    <Typography color="text.secondary">{isAr ? 'لا توجد اختبارات' : 'No exams'}</Typography>
                                ) : (
                                    detail.exams.map((ex) => (
                                        <Box key={ex.exam_id} sx={{ mb: 2, p: 1.5, borderRadius: 1, bgcolor: 'action.hover' }}>
                                            <Typography fontWeight="600">{ex.exam_name}</Typography>
                                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
                                                <Chip size="small" label={`${ex.percent}%`} color={ex.passed ? 'success' : 'error'} />
                                                <Typography variant="caption">
                                                    {isAr ? 'صحيح' : 'Correct'}: {ex.correct_count} — {isAr ? 'خطأ' : 'Wrong'}: {ex.wrong_count}
                                                </Typography>
                                            </Box>
                                            {ex.submitted_at && (
                                                <Typography variant="caption" display="block">{ex.submitted_at}</Typography>
                                            )}
                                        </Box>
                                    ))
                                )}
                            </Box>
                        ) : (
                            <Typography color="text.secondary">{isAr ? 'تعذر تحميل البيانات' : 'Failed to load'}</Typography>
                        )}
                    </DialogContent>
                </Dialog>
            </PageContent>
            <SnackbarAlert open={openSnackBar} message={message} severity={type} onClose={() => setOpenSnackBar(false)} />
        </>
    );
}

export default Students;
